import { Routes } from '@angular/router';
import { BlankComponent } from './layouts/auth-layout/blank.component';
import { FullComponent } from './layouts/main-layout/full.component';
import { authGuard, noAuthGuard, roleGuard } from './core/guards/auth.guard';
import { MI_PERFIL_ACCESS_ROLES } from './core/constants/roles.constants';
import { permiteHorasExtraGuard } from './features/trabajador/horas-extras/guards/permite-horas-extra.guard';
import { permisoLecturaGuard } from './core/guards/permission.guard';

export const routes: Routes = [
  // Ruta de login (sin protecci?n)
  {
    path: 'login',
    component: BlankComponent,
    canActivate: [noAuthGuard],
    data: { title: 'Iniciar sesi?n' },
    loadChildren: () =>
      import('./features/auth/authentication.routes').then((m) => m.AuthenticationRoutes),
  },
  // Verificación pública de firma digital (sin autenticación)
  {
    path: 'verificar/:codigo',
    loadComponent: () =>
      import('./features/verificar-firma/verificar-firma.component').then(
        (m) => m.VerificarFirmaComponent,
      ),
    data: { title: 'Verificar firma digital' },
  },
  // Rutas protegidas
  {
    path: '',
    component: FullComponent,
    canMatch: [authGuard],
    children: [
      {
        path: '',
        redirectTo: '/dashboard',
        pathMatch: 'full',
      },
      // Dashboard
      {
        path: 'dashboard',
        loadChildren: () =>
          import('./features/dashboard/dashboard.routes').then((m) => m.DashboardRoutes),
      },
      // Mi perfil (autoservicio colaborador)
      {
        path: 'mi-perfil',
        canActivate: [roleGuard([...MI_PERFIL_ACCESS_ROLES])],
        loadChildren: () =>
          import('./features/mi-perfil/mi-perfil.routes').then((m) => m.MiPerfilRoutes),
      },
      // Preferencias de cuenta (todos los usuarios autenticados)
      {
        path: 'mi-cuenta',
        loadChildren: () =>
          import('./features/mi-cuenta/mi-cuenta.routes').then((m) => m.MiCuentaRoutes),
      },
      // Centro de soporte
      {
        path: 'soporte',
        loadComponent: () =>
          import('./features/soporte/pages/soporte-page.component').then((m) => m.SoportePageComponent),
        data: {
          title: 'pages.soporte.title',
          breadcrumb: 'pages.soporte.breadcrumb',
        },
      },
      // Demo UI Components
      {
        path: 'demo',
        loadChildren: () => import('./features/demo/demo.routes').then((m) => m.DemoRoutes),
      },
      // Employees/Trabajadores
      {
        path: 'trabajadores',
        children: [
          {
            path: '',
            loadComponent: () =>
              import('@shared').then((m) => m.SubmodulesContainerComponent),
            data: {
              title: 'pages.trabajadores.title',
              subtitle: 'pages.trabajadores.subtitle',
              breadcrumb: 'pages.trabajadores.breadcrumb',
              routes: () => [
                {
                  path: 'gestion-trabajadores',
                  data: {
                    title: 'pages.trabajadores.cards.gestionTrabajadores.title',
                    description: 'pages.trabajadores.cards.gestionTrabajadores.description',
                    icon: 'assets/images/pngs/trabajador.png',
                    theme: 'emerald',
                  },
                },
                {
                  path: 'derecho-habientes',
                  data: {
                    title: 'pages.trabajadores.cards.derechohabientes.title',
                    description: 'pages.trabajadores.cards.derechohabientes.description',
                    icon: 'assets/images/pngs/habiente.png',
                    theme: 'pink',
                  },
                },
                {
                  path: 'control-asistencia',
                  data: {
                    title: 'pages.trabajadores.cards.controlAsistencia.title',
                    description: 'pages.trabajadores.cards.controlAsistencia.description',
                    icon: 'assets/images/svgs/icon-dd-date.svg',
                    theme: 'amber',
                  },
                },
                {
                  path: 'horas-extras',
                  data: {
                    title: 'pages.trabajadores.cards.horasExtras.title',
                    description: 'pages.trabajadores.cards.horasExtras.description',
                    icon: 'assets/images/svgs/contabilidad1.png',
                    theme: 'blue',
                  },
                },
                {
                  path: 'solicitudes',
                  data: {
                    title: 'pages.trabajadores.cards.solicitudes.title',
                    description: 'pages.trabajadores.cards.solicitudes.description',
                    icon: 'assets/images/pngs/planilla2.png',
                    theme: 'indigo',
                  },
                },
                {
                  path: 'vacaciones',
                  data: {
                    title: 'pages.trabajadores.cards.vacaciones.title',
                    description: 'pages.trabajadores.cards.vacaciones.description',
                    icon: 'assets/images/svgs/icon-favorites.svg',
                    theme: 'orange',
                  },
                },
                {
                  path: 'encuestas',
                  data: {
                    title: 'pages.trabajadores.cards.encuestas.title',
                    description: 'pages.trabajadores.cards.encuestas.description',
                    icon: 'assets/images/svgs/icon-speech-bubble.svg',
                    theme: 'cyan',
                  },
                },
                {
                  path: 'gestion-desempeno-objetivos/objetivos',
                  data: {
                    title: 'pages.trabajadores.cards.objetivos.title',
                    description: 'pages.trabajadores.cards.objetivos.description',
                    icon: 'assets/images/svgs/icon-tasks.svg',
                    theme: 'green',
                  },
                },
                {
                  path: 'gestion-desempeno',
                  data: {
                    title: 'pages.trabajadores.cards.desempeno.title',
                    description: 'pages.trabajadores.cards.desempeno.description',
                    icon: 'assets/images/svgs/icon-pie.svg',
                    theme: 'purple',
                  },
                },
              ],
            },
          },
          {
            path: 'gestion-trabajadores',
            canActivate: [permisoLecturaGuard('TRABAJADOR', 'GESTION_TRABAJADORES')],
            data: {
              title: 'pages.trabajadores.gestion.pageTitle',
              breadcrumb: 'pages.trabajadores.gestion.pageTitle',
            },
            loadComponent: () =>
              import('./features/trabajador/gestion-trabajador/components/gestion-trabajadores.component').then(
                (m) => m.GestionTrabajadoresComponent,
              ),
          },
          {
            path: 'derecho-habientes',
            canActivate: [permisoLecturaGuard('TRABAJADOR', 'DERECHO_HABIENTES')],
            loadComponent: () =>
              import('./features/trabajador/derecho-habientes/components/derecho-habientes.component').then(
                (m) => m.DerechoHabientesComponent,
              ),
          },
          {
            path: 'control-asistencia',
            canActivate: [permisoLecturaGuard('TRABAJADOR', 'CONTROL_ASISTENCIA')],
            loadComponent: () =>
              import('./features/trabajador/control-asistencia/components/control-asistencia.component').then(
                (m) => m.ControlAsistenciaComponent,
              ),
            data: {
              title: 'Control de Asistencia',
              breadcrumb: 'Control de Asistencia',
            },
          },
          {
            path: 'horas-extras',
            canActivate: [permiteHorasExtraGuard, permisoLecturaGuard('TRABAJADOR', 'HORAS_EXTRAS')],
            loadComponent: () =>
              import('./features/trabajador/horas-extras/components/horas-extras.component').then(
                (m) => m.HorasExtrasComponent,
              ),
            data: {
              title: 'Horas Extras',
              breadcrumb: 'Horas Extras',
              description: 'Gestión de horas extras y cálculo automático',
            },
          },
          {
            path: 'solicitudes',
            canActivate: [permisoLecturaGuard('TRABAJADOR', 'SOLICITUDES')],
            loadComponent: () =>
              import('./features/trabajador/solicitud/components/solicitudes.component').then(
                (m) => m.SolicitudesComponent,
              ),
          },
          {
            path: 'vacaciones',
            canActivate: [permisoLecturaGuard('TRABAJADOR', 'VACACIONES')],
            loadComponent: () =>
              import('./features/trabajador/vacaciones/components/vacaciones.component').then(
                (m) => m.VacacionesComponent,
              ),
          },
          {
            path: 'capacitaciones',
            canActivate: [permisoLecturaGuard('TRABAJADOR', 'CAPACITACIONES')],
            loadComponent: () =>
              import('./features/trabajador/capacitaciones/components/capacitaciones.component').then(
                (m) => m.CapacitacionesComponent,
              ),
          },
          {
            path: 'gestion-desempeno',
            canActivate: [permisoLecturaGuard('GESTION_DESEMPENO', 'DESEMPENO')],
            loadComponent: () =>
              import('./features/trabajador/gestion-de-desempeño/components/desempeno.component').then(
                (m) => m.DesempenoComponent,
              ),
          },
          {
            path: 'gestion-desempeno-objetivos',
            loadChildren: () =>
              import('./features/trabajador/gestion-desempeno-objetivos/gestion-desempeno-objetivos.routes').then(
                (m) => m.GestionDesempenoObjetivosRoutes,
              ),
          },
           {
            path: 'encuestas',
            canActivate: [permisoLecturaGuard('TRABAJADOR', 'ENCUESTAS')],
            loadComponent: () =>
              import('./features/trabajador/encuestas/components/encuesta.component').then(
                (m) => m.EncuestaComponent,
              ),
          },
          {
            path: 'compromisos',
            canActivate: [permisoLecturaGuard('TRABAJADOR', 'COMPROMISOS')],
            loadComponent: () =>
              import('./features/trabajador/compromisos/components/compromisos.component').then(
                (m) => m.CompromisosComponent,
              ),
          },
          
        ],
      },
      // Core HCM
      {
        path: 'core-hcm',
        children: [
          {
            path: '',
            loadComponent: () =>
              import('@shared').then((m) => m.SubmodulesContainerComponent),
            data: {
              title: 'pages.coreHcm.title',
              subtitle: 'pages.coreHcm.subtitle',
              breadcrumb: 'pages.coreHcm.breadcrumb',
              routes: () => [
                {
                  path: 'personas',
                  data: {
                    title: 'pages.coreHcm.cards.personas.title',
                    description: 'pages.coreHcm.cards.personas.description',
                    icon: 'assets/images/pngs/persona_3d.svg',
                    theme: 'emerald',
                  },
                },
              ],
            },
          },
          {
            path: 'personas',
            loadComponent: () =>
              import('./features/core-hcm/personas/components/personas.component').then(
                (m) => m.PersonasComponent,
              ),
            data: {
              title: 'pages.coreHcm.cards.personas.title',
              breadcrumb: 'pages.coreHcm.cards.personas.title',
            },
          },
        ],
      },

      {
        path: 'equipo',
        children: [
          {
            path: '',
            loadComponent: () =>
              import('@shared').then((m) => m.SubmodulesContainerComponent),
            data: {
              title: 'pages.equipo.title',
              subtitle: 'pages.equipo.subtitle',
              breadcrumb: 'pages.equipo.breadcrumb',
              routes: () => [
                {
                  path: 'reclutamiento',
                  data: {
                    title: 'pages.equipo.cards.reclutamiento.title',
                    description: 'pages.equipo.cards.reclutamiento.description',
                    icon: 'assets/images/pngs/planilla_3d.svg',
                    theme: 'purple',
                  },
                },
                {
                  path: 'gestion-equipo',
                  data: {
                    title: 'pages.equipo.cards.gestionEquipo.title',
                    description: 'pages.equipo.cards.gestionEquipo.description',
                    icon: 'assets/images/svgs/3d_equipo_azul.svg',
                    theme: 'cyan',
                  },
                },
              ],
            },
          },
          {
            path: 'reclutamiento',
            canActivate: [permisoLecturaGuard('EQUIPO', 'RECLUTAMIENTO')],
            loadComponent: () =>
              import('./features/reclutamiento/reclutamiento.component').then(
                (m) => m.ReclutamientoComponent,
              ),
          },
          {
            path: 'gestion-equipo',
            canActivate: [permisoLecturaGuard('EQUIPO', 'GESTION_EQUIPO')],
            loadComponent: () =>
              import('./features/equipo/gestion-equipos/gestion-equipo.component').then(
                (m) => m.GestionEquipoComponent,
              ),
          },
        ],
      },
      {
        path: 'personas',
        children: [
          {
            path: '',
            loadComponent: () =>
              import('@shared').then((m) => m.SubmodulesContainerComponent),
            data: {
              parentPath: '',
              routes: () => [
                {
                  path: 'transferencias',
                  data: {
                    title: 'pages.personas.cards.transferencias.title',
                    breadcrumb: 'pages.personas.cards.transferencias.breadcrumb',
                    description: 'pages.personas.cards.transferencias.description',
                    icon: 'assets/images/pngs/transferencias.svg',
                    theme: 'teal',
                  },
                },
              ],
              title: 'pages.personas.title',
              breadcrumb: 'pages.personas.breadcrumb',
              subtitle: 'pages.personas.subtitle',
              submodulesCarousel: false,
            },
          },
          {
            path: 'transferencias',
            canActivate: [permisoLecturaGuard('PERSONAS', 'TRANSFERENCIAS')],
            loadComponent: () =>
              import('./features/personas/transferencias/components/transferencias.component').then(
                (m) => m.TransferenciasComponent,
              ),
            data: {
              title: 'pages.personas.cards.transferencias.title',
              breadcrumb: 'pages.personas.cards.transferencias.breadcrumb',
              description: 'pages.personas.cards.transferencias.description',
              icon: 'assets/images/pngs/transferencias.svg',
              theme: 'teal',
            },
          },
        ],
      },
      // Planilla
      {
        path: 'planilla',
        loadChildren: () =>
          import('./features/planilla/planilla.routes').then(
            (m) => m.PlanillaRoutes,
          ),
      },
      //organigrama
      {
        path: 'organigrama',
        children: [
          {
            path: '',
            loadComponent: () => import('./features/organigrama/pages/organigrama.component')
              .then(m => m.OrganigramaComponent),
          },
        ],
      },
      // Configuración Global
      {
        path: 'configuracion-global',
        data: { title: 'pages.configuracionGlobal.title', breadcrumb: 'pages.configuracionGlobal.breadcrumb' },
        loadChildren: () =>
          import('./features/configuracion-global/configuracion-global.routes').then(
            (m) => m.ConfiguracionGlobalRoutes,
          ),
      },
      // Configuración
      {
        path: 'configuracion',
        data: { title: 'pages.configuracion.title', breadcrumb: 'pages.configuracion.breadcrumb' },
        loadChildren: () =>
          import('./features/configuracion/configuration.routes').then(
            (m) => m.ConfigurationRoutes,
          ),
      },
      // Sistema
      {
        path: 'sistema',
        data: { title: 'pages.sistema.title', breadcrumb: 'pages.sistema.breadcrumb' },
        loadChildren: () =>
          import('./features/sistema/sistema.routes').then((m) => m.SistemaRoutes),
      },
      // Administración (Usuarios y Roles)
      {
        path: 'administracion',
        data: {
          title: 'pages.administracion.layout.breadcrumb',
          breadcrumb: 'pages.administracion.layout.breadcrumb',
        },
        loadChildren: () =>
          import('./features/administracion/administration.routes').then(
            (m) => m.AdministracionRoutes,
          ),
      },
      // Contabilidad
      {
        path: 'contabilidad',
        loadChildren: () =>
          import('./features/contabilidad/contabilidad.routes').then((m) => m.ContabilidadRoutes),
      },
      // Reportes y BI
      {
        path: 'reportes-bi',
        loadChildren: () =>
          import('./features/reportes-bi/reportes-bi.routes').then((m) => m.ReportesBIRoutes),
      },
      // 401 - Unauthorized
      {
        path: 'no-autorizado',
        loadComponent: () => import('./shared/components/unauthorized.component')
          .then((m) => m.UnauthorizedComponent),
        data: { title: 'No autorizado' },
      },
      // 404 - Not Found
      {
        path: '**',
        data: { title: 'P?gina no encontrada' },
        loadComponent: () =>
          import('./shared/components/not-found.component').then((m) => m.NotFoundComponent),
      },
    ],
  },
];
